import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Avatar, Box, Button, Card, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { MdPhone, MdRefresh, MdOpenInNew, MdCancel } from 'react-icons/md';
import { BiCreditCard, BiTime, BiCamera } from 'react-icons/bi';
import { useRooftop } from '@store';
import { deriveTpStatus } from '../thirdParty/statusDerivation';
import { VENDOR_LABELS, type TpDeliveryState } from '../thirdParty/types';
import CancelCourierDialog from './CancelCourierDialog';

interface CourierRunOverviewProps {
  run: any;
  delivery: TpDeliveryState | null;
  loading: boolean;
  onRefresh: () => void;
}

/** Guest-rides-style stat segment: centered uppercase label over a bold value. */
const DetailStat = ({ label, value, subValue }: { label: string; value: string; subValue?: string }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 500, lineHeight: 1.25, mb: 0.25, textAlign: 'center', textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.25, textAlign: 'center' }}>
      {value}
    </Typography>
    {subValue ? (
      <Typography sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 500, lineHeight: 1.25, mt: 0.25, textAlign: 'center' }}>
        {subValue}
      </Typography>
    ) : null}
  </Box>
);

const centsToDollars = (cents?: number) => (cents != null ? `$${(cents / 100).toFixed(2)}` : '—');

const vehicleLabel = (d?: TpDeliveryState['driver']): string | null => {
  const v = d?.vehicle;
  if (!v) return null;
  const name = [v.make, v.model].filter(Boolean).join(' ');
  return [name, v.license_plate].filter(Boolean).join(' · ') || null;
};

/**
 * Run Overview for courier-fulfilled runs (fulfillment_type external_courier) —
 * replaces the driver/vehicle/departure grid: courier identity, delivery cost,
 * booked-by, live progress strip, and the Booked→Delivered stepper. Data comes
 * from the third-party delivery state, not from mobility_runs.
 */
/** Delivery states where a dealer cancel still makes sense — once the courier
 * has the parts, DoorDash rejects most cancels, so the button hides (the
 * backend stays permissive for ops calls; the vendor is the authority). */
const CANCELABLE_UI_STATUSES = new Set([undefined, null, 'pending', 'driver_assigned', 'arriving_pickup']);

const CourierRunOverview: React.FC<CourierRunOverviewProps> = ({ run, delivery, loading, onRefresh }) => {
  const rooftop = useRooftop();
  const [cancelOpen, setCancelOpen] = useState(false);
  const derived = delivery ? deriveTpStatus(delivery.orchestration_status, delivery.delivery_status) : null;

  const canCancel =
    !!delivery &&
    delivery.orchestration_status === 'BOOKED' &&
    !derived?.terminal &&
    CANCELABLE_UI_STATUSES.has(delivery.delivery_status as string | undefined);
  const runOrderIds: number[] = (run?.stops ?? []).flatMap((stop: { delivery_orders?: { id: number | string }[] }) =>
    (stop.delivery_orders ?? []).map(o => Number(o.id)).filter(id => Number.isFinite(id))
  );

  const etaMinutes =
    delivery?.dropoff_eta && !derived?.terminal
      ? Math.max(0, dayjs(delivery.dropoff_eta).diff(dayjs(), 'minute'))
      : null;
  const pickupAddress = rooftop?.location?.address || rooftop?.address || null;
  const dropoffAddress = run?.stops?.[0]?.address || null;

  const proof = delivery?.proof_of_delivery;
  const hasProof = !!proof && (!!proof.signature_url || (proof.photo_urls?.length ?? 0) > 0 || derived?.stepIndex === 4);

  return (
    <Card data-testid='pd-courier-overview'>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Run Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {derived ? (
              <Chip
                label={derived.label}
                size='small'
                color={derived.error ? 'error' : derived.bucket === 'completed' ? 'success' : 'info'}
                sx={{ fontSize: 12, fontWeight: 600 }}
              />
            ) : null}
            <Tooltip title='Refresh courier status'>
              <IconButton size='small' onClick={onRefresh} disabled={loading} data-testid='pd-courier-refresh'>
                {loading ? <CircularProgress size={16} /> : <MdRefresh size={18} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {!delivery ? (
          <Alert severity={loading ? 'info' : 'warning'}>
            {loading ? 'Loading courier status…' : 'No courier data found for this run yet.'}
          </Alert>
        ) : (
          <>
            {/* Booking failure — the one state that needs dealer action */}
            {derived?.error && delivery.orchestration_status === 'FAILED' ? (
              <Alert severity='error'>
                Booking failed{delivery.last_error?.message ? ` — ${delivery.last_error.message}` : ''}. The orders
                are still assigned to this run; re-dispatch them with your own driver or try a new quote.
              </Alert>
            ) : null}

            {/* Courier · Cost · Booked cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  ⚡ Courier · {VENDOR_LABELS[delivery.vendor]}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1 }}>
                  <Avatar sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 700, bgcolor: 'grey.800' }}>
                    {(delivery.driver?.name || VENDOR_LABELS[delivery.vendor])[0]}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
                      {delivery.driver?.name || 'Awaiting courier assignment'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' noWrap>
                      {vehicleLabel(delivery.driver) || (delivery.driver ? 'Vehicle pending' : 'Courier network is matching')}
                    </Typography>
                  </Box>
                  {delivery.driver?.phone ? (
                    <Tooltip title={`Call ${delivery.driver.name || 'courier'}`}>
                      <IconButton size='small' component='a' href={`tel:${delivery.driver.phone}`}>
                        <MdPhone size={18} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <BiCreditCard size={14} color='#666' />
                  <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Delivery Cost
                  </Typography>
                </Box>
                <Typography variant='h6' sx={{ fontWeight: 800, mt: 0.5 }}>
                  {centsToDollars(delivery.fee_cents)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {VENDOR_LABELS[delivery.vendor]} fee · Billed to run · On Account
                </Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <BiTime size={14} color='#666' />
                  <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Booked
                  </Typography>
                </Box>
                <Typography variant='body2' sx={{ fontWeight: 700, mt: 0.5 }}>
                  {delivery.booked_at ? dayjs(delivery.booked_at).format('M/D/YYYY h:mm A') : '—'}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Quote accepted by {delivery.created_by || run.created_by || '—'}
                </Typography>
              </Box>
            </Box>

            {/* Route (guest-rides style: dot rail, pickup over dropoff) */}
            {pickupAddress || dropoffAddress ? (
              <Box sx={{ display: 'flex', gap: 1.5 }} data-testid='pd-courier-route'>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5, flexShrink: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.800' }} />
                  <Box sx={{ width: 2, height: 17, bgcolor: 'divider', my: 0.25 }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: derived?.bucket === 'completed' ? 'success.main' : 'grey.400' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ lineHeight: 1.25 }} noWrap title={pickupAddress || undefined}>
                    {pickupAddress || 'Pickup address pending'}
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 1.75, lineHeight: 1.25 }} noWrap title={dropoffAddress || undefined}>
                    {dropoffAddress || 'Dropoff address pending'}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {/* Stats strip (guest-rides style: soft surface, centered segments) */}
            <Box sx={{ bgcolor: 'grey.50', borderRadius: '8px', px: 1.5, py: 1 }} data-testid='pd-courier-stats'>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0 }}>
                <DetailStat
                  label='Booked'
                  value={delivery.booked_at ? dayjs(delivery.booked_at).format('M/D h:mm A') : '—'}
                />
                {derived?.terminal ? (
                  <DetailStat
                    label={derived.bucket === 'completed' ? 'Delivered' : 'Ended'}
                    value={delivery.completed_at ? dayjs(delivery.completed_at).format('M/D h:mm A') : derived.label}
                  />
                ) : (
                  <DetailStat
                    label='Est. Delivery'
                    value={etaMinutes !== null ? `${etaMinutes} min` : '—'}
                    subValue={delivery.dropoff_eta ? dayjs(delivery.dropoff_eta).format('h:mm A') : undefined}
                  />
                )}
                <DetailStat
                  label='Status'
                  value={derived?.label ?? '—'}
                  subValue={derived?.returning ? 'parts coming back to the counter' : undefined}
                />
              </Box>
            </Box>

            {/* Courier POD */}
            {hasProof ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, border: '1px dashed', borderColor: 'success.main', borderRadius: 1.5 }}>
                <BiCamera size={20} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    Proof of delivery captured
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {[
                      proof?.photo_urls?.length ? `${proof.photo_urls.length} photo${proof.photo_urls.length !== 1 ? 's' : ''}` : null,
                      proof?.signature_url ? 'signature' : null,
                    ]
                      .filter(Boolean)
                      .join(' + ') || 'Received from courier'}
                    {delivery.completed_at ? ` · ${dayjs(delivery.completed_at).format('h:mm A')}` : ''}
                  </Typography>
                </Box>
                {proof?.signature_url || proof?.photo_urls?.length ? (
                  <Button
                    size='small'
                    component='a'
                    href={proof.signature_url || proof.photo_urls?.[0]}
                    target='_blank'
                    sx={{ textTransform: 'none' }}
                  >
                    View POD
                  </Button>
                ) : null}
              </Box>
            ) : null}

            {/* Tracking + cancel */}
            {delivery.tracking_url || canCancel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {delivery.tracking_url ? (
                  <Button
                    variant='outlined'
                    startIcon={<MdOpenInNew size={16} />}
                    component='a'
                    href={delivery.tracking_url}
                    target='_blank'
                    rel='noreferrer'
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    View full tracking
                  </Button>
                ) : null}
                {canCancel ? (
                  <Button
                    variant='outlined'
                    color='error'
                    startIcon={<MdCancel size={16} />}
                    onClick={() => setCancelOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto' }}
                    data-testid='pd-cancel-courier'
                  >
                    Cancel delivery
                  </Button>
                ) : null}
              </Box>
            ) : null}

            {canCancel ? (
              <CancelCourierDialog
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                partsDeliveryId={Number(delivery.parts_delivery_id)}
                vendorLabel={VENDOR_LABELS[delivery.vendor]}
                orderIds={runOrderIds}
                onSuccess={onRefresh}
              />
            ) : null}
          </>
        )}
      </Box>
    </Card>
  );
};

export default CourierRunOverview;
